<script setup lang="ts">
import emblaCarouselVue from "embla-carousel-vue";
import "photoswipe/style.css";
import type PhotoSwipeLightbox from "photoswipe/lightbox";
import type { Product } from "~~/shared/types/store";

const props = defineProps<{
  product: Product;
  /**
   * Zdjęcia do pokazania zamiast `thumbnail + gallery` produktu — galeria
   * wybranego koloru (BrandConfig.pdpGalleryFollowsColor). Brak = jak dotąd.
   */
  images?: string[];
}>();

const activeIndex = ref(0);
const isMobile = useMediaQuery("(max-width: 767px)", { ssrWidth: 1024 });
const [mobileEmblaRef, mobileEmblaApi] = emblaCarouselVue({
  align: "start",
  containScroll: "trimSnaps",
  skipSnaps: true,
});
const galleryImages = computed(() => {
  const images = (
    props.images ?? [props.product.thumbnail, ...props.product.gallery]
  ).filter(Boolean);
  return [...new Set(images)];
});
const desktopImages = computed(() => galleryImages.value.slice(0, 5));
const mobileImages = computed(() => galleryImages.value);

// Katalog (proles): jedno duże zdjęcie + miniatury zamiast siatki 2 kolumn
// (BrandConfig.pdpGalleryLayout). Miniatury pokazują wszystkie zdjęcia.
const mainThumbs = useBrand().pdpGalleryLayout === "main-thumbs";
const mainImage = computed(
  () => galleryImages.value[activeIndex.value] ?? galleryImages.value[0],
);

// Inny produkt albo inny zestaw zdjęć (zmiana koloru) → od pierwszego zdjęcia.
watch(
  () => [props.product.id, galleryImages.value.join("|")],
  async () => {
    activeIndex.value = 0;
    await nextTick();
    mobileEmblaApi.value?.reInit();
    mobileEmblaApi.value?.scrollTo(0);
  },
);

watch(
  mobileEmblaApi,
  (api) => {
    if (!api) return;

    api.on("select", () => {
      activeIndex.value = api.selectedScrollSnap();
    });
  },
  { immediate: true },
);

// ── Lightbox (PhotoSwipe v5) ──
// Wymiary slajdów: nie znamy intrinsic rozmiaru oryginałów z images.lavmag.eu,
// więc generyczne 3:4 + initialZoomLevel 'fit' (dopasowanie do viewportu).
// Zdjęcia o innych proporcjach (packshoty proles) wpisuje `.pswp__img`
// z object-fit: contain na dole stylów — bez niego byłyby rozciągnięte.
const PSWP_SLIDE_WIDTH = 1200;
const PSWP_SLIDE_HEIGHT = 1600;
let lightbox: PhotoSwipeLightbox | null = null;

function buildSlides() {
  return galleryImages.value.map((src) => ({
    src,
    width: PSWP_SLIDE_WIDTH,
    height: PSWP_SLIDE_HEIGHT,
    alt: props.product.name,
  }));
}

// Strzałki na dużym zdjęciu i karuzeli mobilnej (main-thumbs) — zawijają się
// na końcach jak w quick view. Na mobile przewijają Emblę (jej „select"
// i tak ustawia activeIndex); na desktopie ukrytej karuzeli nie ruszamy.
const GALLERY_NAV = [
  { delta: -1, side: "prev", label: "Poprzednie zdjęcie", path: "M15 18l-6-6 6-6" },
  { delta: 1, side: "next", label: "Następne zdjęcie", path: "M9 18l6-6-6-6" },
] as const;

function showImage(delta: number) {
  const total = galleryImages.value.length;
  if (total <= 1) return;
  activeIndex.value = (activeIndex.value + delta + total) % total;
  if (isMobile.value) mobileEmblaApi.value?.scrollTo(activeIndex.value);
}

function openLightbox(index: number) {
  if (!lightbox) return;
  lightbox.options.dataSource = buildSlides();
  lightbox.loadAndOpen(index);
}

// Tap vs swipe na karuzeli mobilnej — otwieraj lightbox tylko przy realnym tapnięciu.
const pointerDown = { x: 0, y: 0 };
function onMobilePointerDown(e: PointerEvent) {
  pointerDown.x = e.clientX;
  pointerDown.y = e.clientY;
}
function onMobileImageClick(index: number, e: MouseEvent) {
  const movedX = Math.abs(e.clientX - pointerDown.x);
  const movedY = Math.abs(e.clientY - pointerDown.y);
  if (movedX > 8 || movedY > 8) return; // to był przeciąg (swipe), nie tap
  openLightbox(index);
}

// Strażnik: nie inicjuj PhotoSwipe, jeśli komponent odmontowano zanim
// asynchroniczny moduł się załadował (np. szybka nawigacja / teardown w testach) —
// init() sięga do DOM, który mógłby już nie istnieć.
let isActive = true;

onMounted(async () => {
  const { default: PhotoSwipeLightbox } = await import("photoswipe/lightbox");
  if (!isActive) return;
  // Środowisko happy-dom bywa zdemontowane (usuwa globalny `document`) zanim
  // ten dynamiczny import się rozwiąże — test nie demontuje komponentu, więc
  // onBeforeUnmount/isActive tego nie łapie. init() sięga do `document`, więc
  // pomiń, gdy go już nie ma (chroni przed unhandled rejection w testach).
  if (typeof document === "undefined") return;
  lightbox = new PhotoSwipeLightbox({
    dataSource: buildSlides(),
    pswpModule: () => import("photoswipe"),
    initialZoomLevel: "fit",
    secondaryZoomLevel: 1.6,
    maxZoomLevel: 3,
    bgOpacity: 0.92,
  });
  lightbox.init();
});

onBeforeUnmount(() => {
  isActive = false;
  lightbox?.destroy();
  lightbox = null;
});
</script>

<template>
  <div class="gallery" :class="{ 'gallery--main-thumbs': mainThumbs }">
    <div v-if="galleryImages.length === 0" class="gallery__empty">
      <ProductImage
        :src="null"
        :alt="product.name"
        class="gallery__empty-img"
      />
    </div>

    <div v-if="mainThumbs && galleryImages.length > 0" class="gallery__main-thumbs">
      <!-- Strzałki obok (nie w) przycisku zdjęcia — zagnieżdżony <button> jest
           niepoprawny, a klik strzałki nie może otwierać lightboxa. -->
      <div class="gallery__main-frame">
        <button
          type="button"
          class="gallery__main"
          :aria-label="`Powiększ zdjęcie ${activeIndex + 1}`"
          @click="openLightbox(activeIndex)"
        >
          <img
            :src="mainImage"
            :alt="product.name"
            class="gallery__main-img"
          />
        </button>
        <template v-if="galleryImages.length > 1">
          <button
            v-for="nav in GALLERY_NAV"
            :key="nav.side"
            type="button"
            class="gallery__nav"
            :class="`gallery__nav--${nav.side}`"
            :aria-label="nav.label"
            @click="showImage(nav.delta)"
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path :d="nav.path" />
            </svg>
          </button>
        </template>
      </div>
      <div v-if="galleryImages.length > 1" class="gallery__thumbs">
        <button
          v-for="(image, idx) in galleryImages"
          :key="image"
          type="button"
          class="gallery__thumb"
          :class="{ 'is-active': idx === activeIndex }"
          :aria-label="`Pokaż zdjęcie ${idx + 1}`"
          :aria-current="idx === activeIndex ? 'true' : undefined"
          @click="activeIndex = idx"
        >
          <img
            :src="image"
            alt=""
            loading="lazy"
            class="gallery__thumb-small"
          />
        </button>
      </div>
    </div>

    <div v-else-if="desktopImages.length > 0" class="gallery__desktop">
      <button
        v-for="(image, idx) in desktopImages"
        :key="image"
        type="button"
        class="gallery__desktop-item"
        :aria-label="`Powiększ zdjęcie ${idx + 1}`"
        @click="openLightbox(idx)"
      >
        <!-- TYMCZASOWO: surowy <img> zamiast NuxtImg — ładuje pełny oryginał
             (bez re-enkodu IPX), spójnie z kartami produktu. -->
        <img
          v-img-fade
          :src="image"
          :alt="product.name"
          class="gallery__desktop-img"
        />
      </button>
    </div>

    <div
      v-show="isMobile && mobileImages.length > 0"
      ref="mobileEmblaRef"
      class="gallery__mobile gallery__mobile--embla"
    >
      <div class="gallery__mobile-track" @pointerdown="onMobilePointerDown">
        <div
          v-for="(image, idx) in mobileImages"
          :key="image"
          class="gallery__mobile-slide"
          :class="{ 'gallery__mobile-slide--active': activeIndex === idx }"
        >
          <button
            type="button"
            class="gallery__mobile-button"
            :aria-label="`Powiększ zdjęcie ${idx + 1}`"
            @click="onMobileImageClick(idx, $event)"
          >
            <img
              v-img-fade
              :src="image"
              :alt="product.name"
              width="660"
              height="880"
              class="gallery__thumb-img"
            />
          </button>
        </div>
      </div>
      <!-- Poza torem Embla (kontener = pierwsze dziecko viewportu), więc
           strzałki nie łapią przeciągnięcia ani tapnięcia slajdu. -->
      <template v-if="mainThumbs && mobileImages.length > 1">
        <button
          v-for="nav in GALLERY_NAV"
          :key="nav.side"
          type="button"
          class="gallery__nav gallery__nav--mobile"
          :class="`gallery__nav--${nav.side}`"
          :aria-label="nav.label"
          @click="showImage(nav.delta)"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path :d="nav.path" />
          </svg>
        </button>
      </template>
    </div>
  </div>
</template>

<style scoped>
.gallery {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  gap: 8px;
  overflow: hidden;
}

.gallery__img {
  width: 100%;
  max-width: 100%;
  height: auto;
  aspect-ratio: 0.75;
  object-fit: cover;
  object-position: center top;
  display: block;
}

.gallery__empty {
  width: 100%;
  aspect-ratio: 0.75;
  overflow: hidden;
}

.gallery__empty-img {
  width: 100%;
  height: 100%;
}

.gallery__desktop {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 4px;
}

/* Każdy kafel to portret 3:4. Kadrowanie z tokenów marki (themes.css): odzież
   cover + center top (bez ramek, góra kadru / twarz modela zostaje), proles
   contain — całe zdjęcie gadżetu na białym tle kafla. */
.gallery__desktop-item {
  position: relative;
  aspect-ratio: 0.75;
  overflow: hidden;
  background: var(--color-white);
  border: 2px solid transparent;
  padding: 0;
  cursor: pointer;
}

/* Produkt z jednym zdjęciem (np. duplikaty customimage zdedupowane do 1):
   rozciągnij jedyny kafel na obie kolumny zamiast sierocego pół-kafla po lewej. */
.gallery__desktop-item:only-child {
  grid-column: 1 / -1;
}

.gallery__desktop-img {
  width: 100%;
  height: 100%;
  object-fit: var(--product-img-fit);
  object-position: var(--product-img-position);
  display: block;
  /* Kształt brandu (#2): LAVMAG 0, youlook 15px — spójnie z ProductImage.vue. */
  border-radius: var(--product-img-radius);
}

/* ── Katalog (proles): duże zdjęcie + miniatury ──
   Kwadrat, bo packshoty gadżetów to w większości 1:1 albo poziome. Bok nie
   większy niż 72% wysokości ekranu (cały produkt widać bez przewijania) i
   720 px (przy 1920 px kafel 3:4 miał 877 px wysokości). */
.gallery__main-thumbs {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.gallery__main-frame {
  position: relative;
  width: min(100%, 72vh, 720px);
}

.gallery__main {
  display: block;
  width: 100%;
  aspect-ratio: 1;
  overflow: hidden;
  padding: 0;
  border: 0;
  background: var(--color-white);
  cursor: zoom-in;
  border-radius: var(--product-img-radius);
}

.gallery__main-img {
  width: 100%;
  height: 100%;
  object-fit: var(--product-img-fit);
  object-position: var(--product-img-position);
  display: block;
}

.gallery__thumbs {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  width: min(100%, 72vh, 720px);
}

.gallery__thumb {
  width: 72px;
  height: 72px;
  overflow: hidden;
  padding: 0;
  border: 2px solid transparent;
  background: var(--color-white);
  cursor: pointer;
  border-radius: var(--product-img-radius);
  transition: border-color var(--transition-fast);
}

.gallery__thumb:hover:not(.is-active) {
  border-color: var(--color-border);
}

.gallery__thumb.is-active {
  border-color: var(--color-black);
}

/* .gallery ma overflow: hidden — ring fokusa do środka, inaczej ucięty
   (dolny rząd miniatur, boki dużego zdjęcia w wąskiej kolumnie). */
.gallery__main:focus-visible,
.gallery__thumb:focus-visible {
  outline: 2px solid var(--color-black);
  outline-offset: -4px;
}

/* Produkt bez zdjęć: placeholder w kształcie dużego zdjęcia (jak szkielet
   ładowania), nie portret 3:4 na całą kolumnę. */
.gallery--main-thumbs .gallery__empty {
  width: min(100%, 72vh, 720px);
  aspect-ratio: 1;
  align-self: center;
}

.gallery__thumb-small {
  width: 100%;
  height: 100%;
  object-fit: var(--product-img-fit);
  object-position: var(--product-img-position);
  display: block;
}

/* Na mobile karuzela jak w innych markach, ale w kwadratach. */
.gallery--main-thumbs .gallery__mobile-button {
  aspect-ratio: 1;
}

.gallery__mobile-button {
  width: 100%;
  /* Box 3:4 (mediana źródeł odzieży 0.75–0.78); kadrowanie z tokenów marki. */
  aspect-ratio: 0.75;
  overflow: hidden;
  padding: 0;
  border: 0;
  background: var(--color-white);
}

.gallery__thumb-img {
  width: 100%;
  max-width: 100%;
  height: 100%;
  object-fit: var(--product-img-fit);
  object-position: var(--product-img-position);
  display: block;
  /* Kształt brandu (#2): LAVMAG 0, youlook 15px — spójnie z ProductImage.vue. */
  border-radius: var(--product-img-radius);
}

.gallery__mobile {
  overflow: hidden;
}

/* ── Strzałki (main-thumbs) — wygląd jak w quick view (.qv-gallery__nav) ── */
.gallery__nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid var(--color-border-md);
  border-radius: 50%;
  color: var(--color-black);
  cursor: pointer;
  transition:
    background var(--transition-fast),
    color var(--transition-fast),
    border-color var(--transition-fast);
}

/* Tylko z myszą — na dotyku :hover „przykleja się" po tapnięciu. */
@media (hover: hover) {
  .gallery__nav:hover {
    background: var(--color-black);
    color: var(--color-white);
    border-color: var(--color-black);
  }
}

.gallery__nav:focus-visible {
  outline: 2px solid var(--color-black);
  outline-offset: 2px;
}

.gallery__nav--prev {
  left: 12px;
}

.gallery__nav--next {
  right: 12px;
}

.gallery--main-thumbs .gallery__mobile {
  position: relative;
}

.gallery__nav--mobile {
  width: 36px;
  height: 36px;
}

.gallery__nav--mobile.gallery__nav--prev {
  left: 8px;
}

.gallery__nav--mobile.gallery__nav--next {
  right: 8px;
}

.gallery__mobile-track {
  display: flex;
  gap: 8px;
  touch-action: pan-y pinch-zoom;
}

.gallery__mobile-slide {
  flex: 0 0 calc(100% / 1.08);
  min-width: 0;
}

.gallery__mobile :deep(img) {
  -webkit-user-drag: none;
}

@media (max-width: 767px) {
  .gallery__desktop,
  .gallery__main-thumbs {
    display: none;
  }
}

/* Lightbox renderuje się poza komponentem (portal PhotoSwipe), stąd :global.
   PhotoSwipe nadaje <img> wymiary slajdu (zawsze 1200×1600, patrz
   PSWP_SLIDE_*), a jego CSS nie ma object-fit — zdjęcie o innych proporcjach
   było rozciągane (packshot proles 2,27:1 ściśnięty ~3×). contain wpisuje je
   bez zniekształceń; zdjęcia 3:4 wyglądają jak dotąd. */
:global(.pswp__img) {
  object-fit: contain;
}
</style>
